import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { auth, apiClient, ApiError } from "~/lib/api";
import { useAuthStore } from "~/lib/auth-store";
import { AuthShell, Field, inputCls, primaryBtn } from "./auth.sign-in";
export const Route = createFileRoute("/mcp/authorize")({
  validateSearch: (raw) => ({
    request:
      typeof raw.request === "string" && /^[0-9a-f-]{36}$/i.test(raw.request)
        ? raw.request
        : undefined,
  }),
  component: MCPAuthorize,
});
type Consent = {
  id: string;
  client_id: string;
  client_name: string;
  redirect_uri: string;
  scopes: string[];
};
type Connection = {
  id: string;
  name: string;
  oauth_client_id?: string;
  token_issued_at?: string;
  note_scope: "accessible" | "owned";
  status: string;
};
function MCPAuthorize() {
  const { request } = Route.useSearch();
  const store = useAuthStore();
  const [consent, setConsent] = useState<Consent | null>(null),
    [connections, setConnections] = useState<Connection[]>([]),
    [workspace, setWorkspace] = useState(""),
    [connection, setConnection] = useState(""),
    [scope, setScope] = useState("accessible"),
    [write, setWrite] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [email, setEmail] = useState(""),
    [password, setPassword] = useState("");
  useEffect(() => {
    let active = true;
    setConsent(null);
    setWorkspace("");
    setConnection("");
    setWrite(false);
    if (store.status === "authenticated" && request)
      void apiClient
        .get<Consent>("/api/v1/mcp/requests/" + request)
        .then((v) => {
          if (active) setConsent(v);
        })
        .catch(() => {
          if (active)
            setError(
              "This authorization request expired or was already used. Start again from your assistant.",
            );
        });
    return () => {
      active = false;
    };
  }, [request, store.status, store.user?.id]);
  useEffect(() => {
    let active = true;
    setConnections([]);
    setConnection("");
    if (workspace)
      void apiClient
        .get<{ connections: Connection[] }>(
          "/api/v1/tenants/" + workspace + "/connectors",
        )
        .then((v) => {
          if (active)
            setConnections(
              v.connections.filter(
                (c) =>
                  c.status !== "revoked" &&
                  !c.token_issued_at &&
                  (!c.oauth_client_id ||
                    c.oauth_client_id === consent?.client_id),
              ),
            );
        })
        .catch(() => {
          if (active) setError("Could not load workspace access. Retry.");
        });
    return () => {
      active = false;
    };
  }, [workspace, consent?.client_id]);
  async function signIn(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await auth.signIn({ email, password });
      setPassword("");
      await store.setUser(u);
      setError("");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Sign-in unavailable");
    } finally {
      setBusy(false);
    }
  }
  async function submit(deny: boolean) {
    if (!consent) return;
    setBusy(true);
    setError("");
    try {
      let id = connection;
      if (!deny && !id) {
        const c = await apiClient.post<{ id: string }>(
          "/api/v1/tenants/" + workspace + "/connectors",
          {
            name: consent.client_name,
            client_type: "generic",
            note_scope: scope,
            allow_write: false,
          },
        );
        id = c.id;
        setConnection(id);
      }
      const r = await apiClient.post<{ redirect_url: string }>(
        "/api/v1/mcp/requests/" + request + "/approve",
        {
          connection_id: id || "00000000-0000-0000-0000-000000000000",
          deny,
          allow_write: write,
        },
      );
      const target = new URL(r.redirect_url),
        registered = new URL(consent.redirect_uri);
      if (
        target.origin !== registered.origin ||
        target.pathname !== registered.pathname ||
        target.username ||
        target.password ||
        target.hash
      )
        throw Error("Invalid return destination");
      window.location.assign(target.href);
    } catch {
      setError(
        "Authorization did not finish. Refresh access or start again from your assistant.",
      );
      setBusy(false);
    }
  }
  async function different() {
    setBusy(true);
    try {
      await auth.signOut();
      store.clear();
      setError("");
    } catch {
      setError("Could not change account. Retry.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <AuthShell
      title="Connect an assistant to Jotty"
      subtitle="Choose the account, workspace and access you want to share."
    >
      {!request ? (
        <p role="alert">
          Start authorization from your MCP assistant using the server URL in
          Jotty Settings → Connectors.
        </p>
      ) : store.status === "unknown" || store.status === "loading" ? (
        <p>Checking your Jotty session…</p>
      ) : store.status !== "authenticated" ? (
        <form className="flex flex-col gap-4" onSubmit={signIn}>
          <Field label="Email">
            <input
              className={inputCls}
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
          </Field>
          <Field label="Password">
            <input
              className={inputCls}
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </Field>
          <button className={primaryBtn} disabled={busy}>
            Sign in to Jotty
          </button>
        </form>
      ) : consent ? (
        <div className="flex flex-col gap-4">
          <p>Signed in as {store.user?.email}</p>
          <p>
            <strong>{consent.client_name}</strong> is requesting access. Client
            names are self-reported; approve only an assistant you started
            connecting.
          </p>
          <p className="break-all text-sm">
            Return destination: {consent.redirect_uri}
          </p>
          <Field label="Workspace">
            <select
              className={inputCls}
              value={workspace}
              onChange={(e) => setWorkspace(e.target.value)}
              disabled={busy}
            >
              <option value="">Choose a workspace</option>
              {store.tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </Field>
          {workspace && (
            <Field label="Connection">
              <select
                className={inputCls}
                disabled={busy}
                value={connection}
                onChange={(e) => setConnection(e.target.value)}
              >
                <option value="">Create a new connection</option>
                {connections.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ·{" "}
                    {c.note_scope === "owned"
                      ? "Only owned notes"
                      : "Accessible notes"}{" "}
                    · {c.status}
                  </option>
                ))}
              </select>
            </Field>
          )}
          {!connection && (
            <Field label="Readable notes">
              <select
                className={inputCls}
                value={scope}
                onChange={(e) => setScope(e.target.value)}
                disabled={busy}
              >
                <option value="accessible">
                  Notes I can access in this workspace
                </option>
                <option value="owned">Only notes I own</option>
              </select>
            </Field>
          )}
          <p>
            Read access includes meeting notes and your connected calendar. It
            does not start capture, send messages or read your Gmail mailbox.
          </p>
          {consent.scopes.includes("notes:write") && (
            <label className="flex gap-3">
              <input
                type="checkbox"
                checked={write}
                onChange={(e) => setWrite(e.target.checked)}
                disabled={busy}
              />
              Allow appending attributed notes. Existing notes stay intact.
            </label>
          )}
          <button
            className={primaryBtn}
            disabled={busy || !workspace}
            onClick={() => void submit(false)}
          >
            Allow selected access
          </button>
          <button disabled={busy} onClick={() => void submit(true)}>
            Cancel authorization
          </button>
          <button disabled={busy} onClick={() => void different()}>
            Use another Jotty account
          </button>
          <p className="text-sm">
            You can revoke access in Jotty’s connector settings. Changing
            workspace requires a new authorization.
          </p>
        </div>
      ) : (
        <p>Loading authorization…</p>
      )}
      {error && <p role="alert">{error}</p>}
    </AuthShell>
  );
}
